import React, { useState, useEffect, useCallback, useMemo, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Search, ShoppingCart, Menu, X, Package, Truck, MessageCircle, ChevronRight, ChevronLeft, Plus, Minus, Trash2, User, LogOut, LayoutDashboard, Box, FolderOpen, ClipboardList, Settings, Upload, FileSpreadsheet, Calculator, MapPin, Phone, Mail, Clock, Check, AlertCircle, Send, Gift, Share2, Copy, Users, ChevronDown, HelpCircle, FileText, Image, Edit, Save, Cookie, Facebook, Instagram, Twitter, Ruler, Heart } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { CartProvider, useCart } from "./contexts/CartContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CustomerAuthProvider, useCustomerAuth } from "./contexts/CustomerAuthContext";
import CustomerLoginPage from "./pages/CustomerLoginPage";
import CustomerAccountPage from "./pages/CustomerAccountPage";
import AuthCallback from "./pages/AuthCallback";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import { AdminLogin, AdminDashboard } from "./pages/admin/AdminCore";
import { AdminProducts } from "./pages/admin/AdminProductsPage";
import { AdminCategories, AdminOrders, AdminFAQs, AdminCoupons, AdminContent } from "./pages/admin/AdminManage";
import { LiveActivityBar, RecentPurchasePopup, ProductUrgency } from "./components/SocialProof";
import { MobileBottomNav } from "./components/MobileBottomNav";
import { API } from "./config";

// Size Charts Data - Global Standards (UK, EU, US)
const SIZE_CHARTS = {
  clothing: {
    title: "Clothing Size Guide",
    description: "Women's & Men's clothing sizes",
    women: {
      headers: ["Size", "UK", "EU", "US", "Bust (cm)", "Waist (cm)", "Hips (cm)"],
      rows: [
        ["XS", "6", "34", "2", "81-84", "61-64", "86-89"],
        ["S", "8", "36", "4", "84-87", "64-67", "89-92"],
        ["M", "10", "38", "6", "87-92", "67-72", "92-97"],
        ["L", "12", "40", "8", "92-97", "72-77", "97-102"],
        ["XL", "14", "42", "10", "97-102", "77-82", "102-107"],
        ["XXL", "16", "44", "12", "102-107", "82-87", "107-112"],
      ],
      sizeOptions: ["XS", "S", "M", "L", "XL", "XXL"]
    },
    men: {
      headers: ["Size", "UK", "EU", "US", "Chest (cm)", "Waist (cm)"],
      rows: [
        ["XS", "34", "44", "34", "86-91", "71-76"],
        ["S", "36", "46", "36", "91-96", "76-81"],
        ["M", "38", "48", "38", "96-101", "81-86"],
        ["L", "40", "50", "40", "101-106", "86-91"],
        ["XL", "42", "52", "42", "106-111", "91-96"],
        ["XXL", "44", "54", "44", "111-116", "96-101"],
      ],
      sizeOptions: ["XS", "S", "M", "L", "XL", "XXL"]
    }
  },
  footwear: {
    title: "Footwear Size Guide",
    description: "Shoe sizes for men and women",
    women: {
      headers: ["UK", "EU", "US", "Foot Length (cm)"],
      rows: [
        ["3", "36", "5.5", "22.0"],
        ["4", "37", "6.5", "22.9"],
        ["5", "38", "7.5", "23.7"],
        ["6", "39", "8.5", "24.6"],
        ["7", "40", "9.5", "25.4"],
        ["8", "41", "10.5", "26.2"],
      ],
      sizeOptions: ["UK 3", "UK 4", "UK 5", "UK 6", "UK 7", "UK 8"]
    },
    men: {
      headers: ["UK", "EU", "US", "Foot Length (cm)"],
      rows: [
        ["6", "40", "7", "24.6"],
        ["7", "41", "8", "25.4"],
        ["8", "42", "9", "26.2"],
        ["9", "43", "10", "27.1"],
        ["10", "44", "11", "27.9"],
        ["11", "45", "12", "28.8"],
        ["12", "46", "13", "29.6"],
      ],
      sizeOptions: ["UK 6", "UK 7", "UK 8", "UK 9", "UK 10", "UK 11", "UK 12"]
    }
  },
  rings: {
    title: "Ring Size Guide",
    description: "International ring sizes",
    unisex: {
      headers: ["UK", "EU", "US", "Inner Diameter (mm)", "Inner Circumference (mm)"],
      rows: [
        ["H", "46", "4", "14.9", "46.8"],
        ["I", "47", "4.5", "15.3", "48.0"],
        ["J", "48", "5", "15.7", "49.3"],
        ["K", "49", "5.5", "16.1", "50.6"],
        ["L", "50", "6", "16.5", "51.9"],
        ["M", "51", "6.5", "16.9", "53.1"],
        ["N", "52", "7", "17.3", "54.4"],
        ["O", "54", "7.5", "17.7", "55.7"],
        ["P", "55", "8", "18.1", "57.0"],
        ["Q", "56", "8.5", "18.5", "58.3"],
        ["R", "58", "9", "18.9", "59.5"],
        ["S", "59", "9.5", "19.4", "60.8"],
        ["T", "60", "10", "19.8", "62.1"],
        ["U", "61", "10.5", "20.2", "63.4"],
        ["V", "62", "11", "20.6", "64.6"],
      ],
      sizeOptions: ["H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V"]
    }
  },
  chains: {
    title: "Chain/Necklace Length Guide",
    description: "Standard necklace and chain lengths",
    unisex: {
      headers: ["Length", "Inches", "cm", "Style/Fit"],
      rows: [
        ["Choker", "14\"", "35", "Sits tight on neck"],
        ["Princess", "16\"", "40", "Sits at collarbone"],
        ["Short", "18\"", "45", "Sits below collarbone"],
        ["Matinee", "20\"", "50", "Sits at or above neckline"],
        ["Standard", "22\"", "55", "Sits below neckline"],
        ["Opera", "24\"", "60", "Sits at breastbone"],
        ["Long", "28\"", "70", "Below breastbone"],
        ["Rope", "30\"+", "76+", "Below bust line"],
      ],
      sizeOptions: ["14\" Choker", "16\" Princess", "18\" Short", "20\" Matinee", "22\" Standard", "24\" Opera", "28\" Long", "30\"+ Rope"]
    }
  },
  bracelets: {
    title: "Bracelet Size Guide",
    description: "Wrist sizes for bracelets and bangles",
    unisex: {
      headers: ["Size", "Wrist (cm)", "Wrist (inches)", "Bracelet Length"],
      rows: [
        ["XS", "14-15", "5.5-6\"", "16-17 cm"],
        ["S", "15-16", "6-6.5\"", "17-18 cm"],
        ["M", "16-17", "6.5-6.75\"", "18-19 cm"],
        ["L", "17-18", "6.75-7\"", "19-20 cm"],
        ["XL", "18-19", "7-7.5\"", "20-21 cm"],
        ["XXL", "19-20", "7.5-8\"", "21-22 cm"],
      ],
      sizeOptions: ["XS", "S", "M", "L", "XL", "XXL"]
    }
  }
};

// Get size options for a product type
const getSizeOptionsForType = (sizeType, gender = "women") => {
  const chart = SIZE_CHARTS[sizeType];
  if (!chart) return [];
  if (chart.unisex) return chart.unisex.sizeOptions;
  return chart[gender]?.sizeOptions || chart.women?.sizeOptions || [];
};

// Size type options for admin dropdown
const SIZE_TYPE_OPTIONS = [
  { value: "clothing", label: "Clothing (Dresses, Shirts, etc.)" },
  { value: "footwear", label: "Footwear (Shoes, Sneakers, etc.)" },
  { value: "rings", label: "Rings & Bands" },
  { value: "chains", label: "Chains & Necklaces" },
  { value: "bracelets", label: "Bracelets & Bangles" },
];

// Size Chart Component
const SizeChartModal = ({ productType, isOpen, onClose }) => {
  const [activeGender, setActiveGender] = useState("women");
  
  // Determine which size chart to show based on product type/category
  const getChartType = (type) => {
    const typeLower = (type || "").toLowerCase();
    if (typeLower.includes("ring")) return "rings";
    if (typeLower.includes("chain") || typeLower.includes("necklace") || typeLower.includes("pendant")) return "chains";
    if (typeLower.includes("bracelet") || typeLower.includes("bangle")) return "bracelets";
    if (typeLower.includes("shoe") || typeLower.includes("sneaker") || typeLower.includes("boot") || typeLower.includes("sandal") || typeLower.includes("heel") || typeLower.includes("footwear")) return "footwear";
    if (typeLower.includes("dress") || typeLower.includes("shirt") || typeLower.includes("top") || typeLower.includes("jacket") || typeLower.includes("coat") || typeLower.includes("trouser") || typeLower.includes("pant") || typeLower.includes("skirt") || typeLower.includes("clothing")) return "clothing";
    return null;
  };

  const chartType = getChartType(productType);
  const chart = chartType ? SIZE_CHARTS[chartType] : null;

  if (!isOpen || !chart) return null;

  const hasGenderOptions = chart.women && chart.men;
  const chartData = hasGenderOptions ? chart[activeGender] : chart.unisex;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black uppercase flex items-center gap-2">
            <Ruler className="w-6 h-6 text-[#FF6B00]" />
            {chart.title}
          </DialogTitle>
        </DialogHeader>
        
        <p className="text-gray-600 mb-4">{chart.description}</p>
        
        {/* Gender Toggle for clothing/footwear */}
        {hasGenderOptions && (
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setActiveGender("women")}
              className={`px-6 py-2 font-bold transition-colors ${activeGender === "women" ? "bg-[#FF6B00] text-[#0A0A0A]" : "bg-[#F5F5F5] hover:bg-[#E5E5E5]"}`}
            >
              Women
            </button>
            <button
              onClick={() => setActiveGender("men")}
              className={`px-6 py-2 font-bold transition-colors ${activeGender === "men" ? "bg-[#FF6B00] text-[#0A0A0A]" : "bg-[#F5F5F5] hover:bg-[#E5E5E5]"}`}
            >
              Men
            </button>
          </div>
        )}

        {/* Size Chart Table */}
        <div className="overflow-x-auto border-2 border-[#0A0A0A]">
          <table className="w-full">
            <thead>
              <tr className="bg-[#0A0A0A] text-white">
                {chartData.headers.map((header, idx) => (
                  <th key={`header-${header}`} className="px-4 py-3 text-left text-sm font-bold uppercase">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.rows.map((row, rowIdx) => (
                <tr key={rowIdx} className={rowIdx % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]"}>
                  {row.map((cell, cellIdx) => (
                    <td key={cellIdx} className={`px-4 py-3 text-sm ${cellIdx === 0 ? "font-bold" : ""}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Measurement Tips */}
        <div className="mt-6 bg-[#F5F5F5] p-4 border-l-4 border-[#FF6B00]">
          <h4 className="font-bold mb-2">How to Measure</h4>
          {chartType === "rings" && (
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Wrap a string or paper strip around your finger</li>
              <li>• Mark where it overlaps and measure the length in mm</li>
              <li>• Compare with the circumference column above</li>
              <li>• If between sizes, choose the larger size</li>
            </ul>
          )}
          {chartType === "chains" && (
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Use a soft measuring tape around your neck</li>
              <li>• Add 2-4 inches for comfortable fit</li>
              <li>• Consider the pendant size when choosing length</li>
            </ul>
          )}
          {chartType === "footwear" && (
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Stand on paper and trace your foot</li>
              <li>• Measure from heel to longest toe</li>
              <li>• Measure both feet - use the larger measurement</li>
              <li>• Measure at the end of the day when feet are largest</li>
            </ul>
          )}
          {chartType === "clothing" && (
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• <strong>Bust/Chest:</strong> Measure around fullest part</li>
              <li>• <strong>Waist:</strong> Measure at natural waistline</li>
              <li>• <strong>Hips:</strong> Measure around fullest part</li>
              <li>• If between sizes, size up for comfort</li>
            </ul>
          )}
          {chartType === "bracelets" && (
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Measure your wrist with a flexible tape</li>
              <li>• Add 1-2 cm for a comfortable fit</li>
              <li>• For bangles, measure over your knuckles</li>
            </ul>
          )}
        </div>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500">Need help? Chat with our AI assistant or contact us at +234 915 959 6647</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Helper function to check if product needs size chart
const productNeedsSizeChart = (product) => {
  const searchTerms = ["ring", "chain", "necklace", "pendant", "bracelet", "bangle", "shoe", "sneaker", "boot", "sandal", "heel", "footwear", "dress", "shirt", "top", "jacket", "coat", "trouser", "pant", "skirt", "clothing", "fashion"];
  const productText = `${product.name} ${product.category_name} ${product.description}`.toLowerCase();
  return searchTerms.some(term => productText.includes(term));
};
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { ScrollArea } from "./components/ui/scroll-area";
import { Separator } from "./components/ui/separator";

// Logo Component
const Logo = ({ size = "default" }) => (
  <Link to="/" className="flex items-center">
    <img
      src="https://customer-assets.emergentagent.com/job_ukok-trade-hub/artifacts/lytbo5x7_Gemini_Generated_Image_9eoaym9eoaym9eoa.jpeg"
      alt="UKOK"
      className={size === "small" ? "h-10" : "h-12 md:h-14"}
    />
  </Link>
);

// Navigation
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { cartCount } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/categories`).then((res) => setCategories(res.data)).catch(console.error);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      <LiveActivityBar />
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b-2 border-[#0A0A0A]" data-testid="main-navigation">
        <div className="container-main">
          <div className="flex items-center justify-between h-14 md:h-20">
            <Logo />

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <Link to="/" className="nav-link">Home</Link>
              {categories.slice(0, 4).map((cat) => (
                <Link key={cat.id} to={`/category/${cat.slug}`} className="nav-link">{cat.name}</Link>
              ))}
              <Link to="/track" className="nav-link">Track Order</Link>
              <Link to="/refer" className="nav-link text-[#FF6B00]">Share & Earn</Link>
            </div>

            {/* Desktop Search Bar */}
            <form onSubmit={handleSearch} className="hidden md:flex items-center gap-2 flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#F5F5F5] border-0 h-11"
                  data-testid="search-input"
                />
              </div>
            </form>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              {/* Mobile search icon */}
              <Link to="/search" className="md:hidden" data-testid="mobile-search-icon">
                <Search className="w-6 h-6" />
              </Link>

              <Link to="/account" className="hidden md:flex items-center gap-2 text-sm font-bold hover:text-[#FF6B00]" data-testid="account-link">
                <User className="w-5 h-5" />
              </Link>

              <Sheet>
                <SheetTrigger asChild>
                  <button className="relative" data-testid="cart-button">
                    <ShoppingCart className="w-6 h-6" />
                    {cartCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-[#FF6B00] text-[#0A0A0A] text-xs font-bold w-5 h-5 flex items-center justify-center">
                        {cartCount}
                      </span>
                    )}
                  </button>
                </SheetTrigger>
                <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
                  <CartDrawer />
                </SheetContent>
              </Sheet>

              <button
                className="lg:hidden hidden"
                onClick={() => setMobileMenuOpen(true)}
                data-testid="mobile-menu-button"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu lg:hidden">
          <div className="flex items-center justify-between p-4 border-b-2 border-[#0A0A0A]">
            <Logo size="small" />
            <button onClick={() => setMobileMenuOpen(false)}>
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="p-4">
            <form onSubmit={handleSearch} className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#F5F5F5] border-0"
                />
              </div>
            </form>
            <div className="space-y-4">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block text-lg font-bold">Home</Link>
              {categories.map((cat) => (
                <Link key={cat.id} to={`/category/${cat.slug}`} onClick={() => setMobileMenuOpen(false)} className="block text-lg font-bold">{cat.name}</Link>
              ))}
              <Link to="/track" onClick={() => setMobileMenuOpen(false)} className="block text-lg font-bold">Track Order</Link>
              <Link to="/refer" onClick={() => setMobileMenuOpen(false)} className="block text-lg font-bold text-[#FF6B00]">Share & Earn ₦500</Link>
              <Separator />
              <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="block text-lg font-bold">Admin</Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Cart Drawer
const CartDrawer = () => {
  const { cart, removeFromCart, updateQuantity, cartTotal, clearCart } = useCart();

  const formatPrice = (price) => `₦${price.toLocaleString()}`;

  if (cart.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <ShoppingCart className="w-16 h-16 text-gray-300 mb-4" />
        <h3 className="text-xl font-bold mb-2">Your cart is empty</h3>
        <p className="text-gray-500 mb-6">Add some products to get started</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <SheetHeader>
        <SheetTitle className="text-2xl font-black uppercase tracking-tight">Your Cart ({cart.length})</SheetTitle>
      </SheetHeader>
      <ScrollArea className="flex-1 my-4">
        <div className="space-y-4 pr-4">
          {cart.map((item, idx) => (
            <div key={`${item.id}-${item.selectedSize || idx}`} className="flex gap-4 p-4 bg-[#F5F5F5]" data-testid={`cart-item-${item.id}`}>
              <img src={item.images?.[0] || "/placeholder.jpg"} alt={item.name} className="w-20 h-20 object-cover" />
              <div className="flex-1">
                <h4 className="font-bold text-sm">{item.name}</h4>
                {item.selectedSize && (
                  <p className="text-xs text-[#FF6B00] font-bold">Size: {item.selectedSize}</p>
                )}
                <p className="text-[#FF6B00] font-bold">{formatPrice(item.selling_price)}</p>
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={() => updateQuantity(item.id, item.quantity - 1, item.selectedSize)} className="p-1 border border-[#0A0A0A]">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="font-bold w-8 text-center">{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.id, item.quantity + 1, item.selectedSize)} className="p-1 border border-[#0A0A0A]">
                    <Plus className="w-4 h-4" />
                  </button>
                  <button onClick={() => removeFromCart(item.id, item.selectedSize)} className="ml-auto text-red-500">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
      <div className="border-t-2 border-[#0A0A0A] pt-4 space-y-4">
        <div className="flex justify-between text-lg font-bold">
          <span>Subtotal</span>
          <span>{formatPrice(cartTotal)}</span>
        </div>
        <p className="text-sm text-green-600 font-bold">FREE Shipping</p>
        <p className="text-xs text-gray-400">Delivery from GIG locations may attract a fee</p>
        <SheetClose asChild>
          <Link to="/checkout" className="w-full">
            <Button className="w-full btn-primary h-12" data-testid="checkout-button">
              Proceed to Checkout
            </Button>
          </Link>
        </SheetClose>
        <button onClick={clearCart} className="w-full text-sm text-gray-500 hover:text-red-500">Clear Cart</button>
      </div>
    </div>
  );
};

// Product Card
const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const formatPrice = (price) => `₦${price.toLocaleString()}`;

  return (
    <div className="product-card group" data-testid={`product-card-${product.id}`}>
      <Link to={`/product/${product.slug}`}>
        <div className="aspect-square bg-[#F5F5F5] overflow-hidden">
          <img
            src={product.images?.[0] || "/placeholder.jpg"}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      </Link>
      <div className="p-4">
        <p className="text-xs text-[#525252] uppercase tracking-wider mb-1">{product.category_name}</p>
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-bold text-lg mb-2 line-clamp-2 hover:text-[#FF6B00]">{product.name}</h3>
        </Link>
        <div className="flex items-center justify-between">
          <span className="text-[#FF6B00] font-black text-xl">{formatPrice(product.selling_price)}</span>
          <button
            onClick={() => addToCart(product)}
            className="btn-secondary text-xs px-4 py-2"
            data-testid={`add-to-cart-${product.id}`}
            disabled={product.stock < 1}
          >
            {product.stock < 1 ? "Out of Stock" : "Add"}
          </button>
        </div>
        <p className="text-xs text-green-600 font-bold mt-1">Free Shipping</p>
        {product.stock > 0 && product.stock < 10 && (
          <p className="text-xs text-red-500 mt-2">Only {product.stock} left!</p>
        )}
      </div>
    </div>
  );
};

// Product Slider (Hero)
const ProductSlider = ({ products }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (products.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % products.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [products.length]);

  if (products.length === 0) return null;

  const currentProduct = products[currentIndex];
  const formatPrice = (price) => `₦${price.toLocaleString()}`;

  return (
    <div className="relative bg-[#0A0A0A] text-white overflow-hidden" data-testid="hero-slider">
      <div className="container-main py-12 md:py-24">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="order-2 md:order-1">
            <p className="text-[#FF6B00] uppercase tracking-[0.2em] text-sm font-bold mb-4">
              {currentProduct.category_name}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter mb-6">
              {currentProduct.name}
            </h1>
            <p className="text-gray-400 text-lg mb-6 line-clamp-2">
              {currentProduct.description}
            </p>
            <div className="flex items-center gap-6 mb-8">
              <span className="text-3xl font-black text-[#FF6B00]">{formatPrice(currentProduct.selling_price)}</span>
              {currentProduct.stock > 0 && (
                <span className="text-sm text-gray-400">In Stock</span>
              )}
            </div>
            <div className="flex gap-4">
              <Link to={`/product/${currentProduct.slug}`}>
                <Button className="btn-primary" data-testid="shop-now-button">Shop Now</Button>
              </Link>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <div className="aspect-square bg-[#F5F5F5] relative">
              <img
                src={currentProduct.images?.[0] || "/placeholder.jpg"}
                alt={currentProduct.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
        
        {/* Slider Controls */}
        <div className="flex items-center justify-between mt-8">
          <div className="flex gap-2">
            {products.slice(0, 5).map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-12 h-1 transition-colors ${idx === currentIndex ? "bg-[#FF6B00]" : "bg-gray-600"}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentIndex((prev) => (prev - 1 + products.length) % products.length)}
              className="w-12 h-12 border-2 border-white flex items-center justify-center hover:bg-[#FF6B00] hover:border-[#FF6B00] transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => setCurrentIndex((prev) => (prev + 1) % products.length)}
              className="w-12 h-12 border-2 border-white flex items-center justify-center hover:bg-[#FF6B00] hover:border-[#FF6B00] transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Home Page
// Category Section with hero banner + horizontal scroll products
const CategorySection = ({ category, index }) => {
  const scrollRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
  };

  useEffect(() => { checkScroll(); }, [category.products]);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir === "left" ? -320 : 320, behavior: "smooth" });
    setTimeout(checkScroll, 400);
  };

  const isEven = index % 2 === 0;

  return (
    <section className={`py-10 md:py-16 ${isEven ? "bg-white" : "bg-[#FAFAFA]"}`} data-testid={`category-section-${category.slug}`}>
      <div className="container-main">
        {/* Category Hero Banner */}
        <Link to={`/category/${category.slug}`} className="block group relative mb-8 overflow-hidden" style={{ height: "180px" }}>
          <div className="absolute inset-0 bg-[#0A0A0A]">
            {category.image_url && (
              <img
                src={category.image_url}
                alt={category.name}
                className="w-full h-full object-cover opacity-40 group-hover:opacity-30 group-hover:scale-105 transition-all duration-500"
              />
            )}
            {!category.image_url && (
              <div className="w-full h-full" style={{
                background: index % 4 === 0 ? "linear-gradient(135deg, #0A0A0A 0%, #1a1a2e 100%)" :
                  index % 4 === 1 ? "linear-gradient(135deg, #0A0A0A 0%, #16213e 100%)" :
                  index % 4 === 2 ? "linear-gradient(135deg, #0A0A0A 0%, #1a1a1a 100%)" :
                  "linear-gradient(135deg, #0A0A0A 0%, #2d132c 100%)"
              }} />
            )}
          </div>
          <div className="absolute inset-0 flex items-center px-8 md:px-12">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-1 bg-[#FF6B00]" />
                <span className="text-[#FF6B00] text-xs font-bold uppercase tracking-widest">UK to Nigeria</span>
              </div>
              <h2 className="text-white text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight">{category.name}</h2>
              {category.description && (
                <p className="text-gray-400 text-sm mt-2 max-w-lg">{category.description}</p>
              )}
              <span className="inline-flex items-center text-white text-sm font-bold mt-3 group-hover:text-[#FF6B00] transition-colors">
                View All {category.products?.length || 0} Products <ChevronRight className="w-4 h-4 ml-1" />
              </span>
            </div>
          </div>
        </Link>

        {/* Horizontal Scroll Products */}
        <div className="relative">
          {/* Scroll Arrows */}
          {canScrollLeft && (
            <button
              onClick={() => scroll("left")}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-[#0A0A0A] text-white flex items-center justify-center hover:bg-[#FF6B00] transition-colors shadow-lg -ml-2"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {canScrollRight && (
            <button
              onClick={() => scroll("right")}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-[#0A0A0A] text-white flex items-center justify-center hover:bg-[#FF6B00] transition-colors shadow-lg -mr-2"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}

          {/* Scrollable Product Row */}
          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex gap-4 overflow-x-auto scroll-smooth pb-4 hide-scrollbar"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {category.products.map((product) => (
              <div key={product.id} className="flex-shrink-0 w-[220px] sm:w-[240px] md:w-[260px]">
                <ProductCard product={product} />
              </div>
            ))}

            {/* View All Card */}
            <Link
              to={`/category/${category.slug}`}
              className="flex-shrink-0 w-[220px] sm:w-[240px] md:w-[260px] border-2 border-dashed border-[#E5E5E5] hover:border-[#FF6B00] flex flex-col items-center justify-center transition-colors group"
              style={{ minHeight: "300px" }}
            >
              <ChevronRight className="w-10 h-10 text-gray-300 group-hover:text-[#FF6B00] transition-colors" />
              <span className="font-bold text-sm text-gray-400 group-hover:text-[#FF6B00] mt-2 transition-colors">
                View All
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  const [categorySections, setCategorySections] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/categories-with-products`),
      axios.get(`${API}/products/featured`)
    ])
      .then(([sectionsRes, featuredRes]) => {
        setCategorySections(sectionsRes.data);
        setFeaturedProducts(featuredRes.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="home-page">
      {/* Hero Slider */}
      {featuredProducts.length > 0 && <ProductSlider products={featuredProducts} />}

      {/* Category Sections */}
      {categorySections.map((section, idx) => (
        <CategorySection key={section.id} category={section} index={idx} />
      ))}

      {/* Referral CTA */}
      <section className="py-12 bg-[#FF6B00]">
        <div className="container-main">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-black text-[#0A0A0A] uppercase">Share & Earn ₦500</h3>
              <p className="text-[#0A0A0A]/80">Refer friends to UKOK and earn rewards for every purchase they make!</p>
            </div>
            <Link to="/refer">
              <Button className="btn-secondary whitespace-nowrap">Start Earning</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-[#0A0A0A] text-white">
        <div className="container-main">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <Truck className="w-12 h-12 mx-auto mb-4 text-[#FF6B00]" />
              <h3 className="text-xl font-bold mb-2">UK to Nigeria Shipping</h3>
              <p className="text-gray-400">Fast and reliable international delivery</p>
            </div>
            <div className="text-center">
              <Package className="w-12 h-12 mx-auto mb-4 text-[#FF6B00]" />
              <h3 className="text-xl font-bold mb-2">Quality Products</h3>
              <p className="text-gray-400">Sourced directly from UK suppliers</p>
            </div>
            <div className="text-center">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 text-[#FF6B00]" />
              <h3 className="text-xl font-bold mb-2">24/7 AI Support</h3>
              <p className="text-gray-400">Get instant answers to your questions</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Category Page
const CategoryPage = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/categories/${slug}`)
      .then((res) => {
        setCategory(res.data);
        return axios.get(`${API}/products?category_id=${res.data.id}`);
      })
      .then((res) => setProducts(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="container-main py-24 text-center">
        <h1 className="text-4xl font-black">Category Not Found</h1>
      </div>
    );
  }

  return (
    <div className="py-8 md:py-16" data-testid="category-page">
      <div className="container-main">
        <div className="mb-12">
          <p className="text-sm text-[#525252] uppercase tracking-wider mb-2">Category</p>
          <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter">{category.name}</h1>
          {category.description && (
            <p className="text-lg text-gray-600 mt-4 max-w-2xl">{category.description}</p>
          )}
        </div>

        {products.length === 0 ? (
          <div className="text-center py-24 bg-[#F5F5F5]">
            <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-bold mb-2">No products yet</h3>
            <p className="text-gray-500">Check back soon for new arrivals!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Product Detail Page
// Wishlist Button
const WishlistButton = ({ productId }) => {
  const { customer, customerAxios } = useCustomerAuth();
  const [inWishlist, setInWishlist] = useState(false);

  useEffect(() => {
    if (!customer) return;
    customerAxios.get("/customer/wishlist")
      .then((res) => setInWishlist(res.data.some((p) => p.id === productId)))
      .catch(() => {});
  }, [customer, customerAxios, productId]);

  const toggle = async () => {
    if (!customer) { window.location.href = "/login"; return; }
    try {
      if (inWishlist) {
        await customerAxios.delete(`/customer/wishlist/${productId}`);
        setInWishlist(false);
        toast.success("Removed from wishlist");
      } else {
        await customerAxios.post(`/customer/wishlist/${productId}`);
        setInWishlist(true);
        toast.success("Added to wishlist!");
      }
    } catch { toast.error("Error updating wishlist"); }
  };

  return (
    <button onClick={toggle} className={`flex items-center gap-2 mt-4 text-sm font-bold hover:text-[#FF6B00] transition-colors ${inWishlist ? "text-red-500" : "text-gray-500"}`} data-testid="wishlist-button">
      <Heart className={`w-5 h-5 ${inWishlist ? "fill-current" : ""}`} />
      {inWishlist ? "In Wishlist" : "Add to Wishlist"}
    </button>
  );
};

const ProductDetailPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const { addToCart } = useCart();

  useEffect(() => {
    axios.get(`${API}/products/${slug}`)
      .then((res) => setProduct(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container-main py-24 text-center">
        <h1 className="text-4xl font-black">Product Not Found</h1>
      </div>
    );
  }

  const formatPrice = (price) => `₦${price.toLocaleString()}`;
  const needsSizeChart = productNeedsSizeChart(product) || product.has_sizes;
  const hasSizes = product.has_sizes && product.sizes && product.sizes.length > 0;
  
  // Get stock for selected size or total stock
  const getAvailableStock = () => {
    if (hasSizes && selectedSize) {
      const sizeInfo = product.sizes.find(s => s.size === selectedSize);
      return sizeInfo?.stock || 0;
    }
    return product.stock;
  };

  const availableStock = getAvailableStock();
  const canAddToCart = hasSizes ? (selectedSize && availableStock > 0) : availableStock > 0;

  const handleAddToCart = () => {
    if (hasSizes && !selectedSize) {
      toast.error("Please select a size");
      return;
    }
    addToCart(product, quantity, selectedSize);
  };

  return (
    <div className="py-8 md:py-16" data-testid="product-detail-page">
      <div className="container-main">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
          {/* Images */}
          <div>
            <div className="aspect-square bg-[#F5F5F5] mb-4">
              <img
                src={product.images?.[selectedImage] || "/placeholder.jpg"}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-2">
                {product.images.map((img, idx) => (
                  <button
                    key={img}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 border-2 ${idx === selectedImage ? "border-[#FF6B00]" : "border-transparent"}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <p className="text-sm text-[#525252] uppercase tracking-wider mb-2">{product.category_name}</p>
            <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight mb-4">{product.name}</h1>
            <p className="text-4xl font-black text-[#FF6B00] mb-2">{formatPrice(product.selling_price)}</p>
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1">FREE SHIPPING</span>
              <span className="text-xs text-gray-400">GIG delivery fee may apply</span>
            </div>
            <p className="text-gray-600 leading-relaxed mb-6">{product.description}</p>

            {/* Size Selection */}
            {hasSizes && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <Label className="font-bold text-lg">Select Size</Label>
                  {needsSizeChart && (
                    <button
                      onClick={() => setShowSizeChart(true)}
                      className="flex items-center gap-1 text-[#FF6B00] text-sm font-bold hover:underline"
                      data-testid="size-chart-button"
                    >
                      <Ruler className="w-4 h-4" />
                      Size Guide
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((sizeInfo) => {
                    const isOutOfStock = sizeInfo.stock <= 0;
                    const isSelected = selectedSize === sizeInfo.size;
                    return (
                      <button
                        key={sizeInfo.size}
                        onClick={() => !isOutOfStock && setSelectedSize(sizeInfo.size)}
                        disabled={isOutOfStock}
                        className={`
                          px-4 py-3 border-2 font-bold text-sm transition-all min-w-[60px]
                          ${isSelected ? "border-[#FF6B00] bg-[#FF6B00] text-[#0A0A0A]" : "border-[#0A0A0A]"}
                          ${isOutOfStock ? "opacity-40 cursor-not-allowed line-through" : "hover:border-[#FF6B00]"}
                        `}
                        data-testid={`size-option-${sizeInfo.size}`}
                      >
                        {sizeInfo.size}
                        {sizeInfo.stock <= 3 && sizeInfo.stock > 0 && (
                          <span className="block text-xs text-red-500 font-normal">Only {sizeInfo.stock} left</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {selectedSize && (
                  <p className="text-sm text-green-600 mt-2">
                    ✓ Size {selectedSize} selected - {availableStock} in stock
                  </p>
                )}
              </div>
            )}

            {/* Size Chart Button for products without size variants but needing guide */}
            {!hasSizes && needsSizeChart && (
              <button
                onClick={() => setShowSizeChart(true)}
                className="flex items-center gap-2 text-[#FF6B00] font-bold mb-6 hover:underline"
                data-testid="size-chart-button"
              >
                <Ruler className="w-5 h-5" />
                View Size Guide (UK / EU / US)
              </button>
            )}

            {canAddToCart ? (
              <>
                <div className="flex items-center gap-4 mb-6">
                  <Label className="font-bold">Quantity</Label>
                  <div className="flex items-center border-2 border-[#0A0A0A]">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-3 hover:bg-[#F5F5F5]"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="px-6 font-bold">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(availableStock, quantity + 1))}
                      className="p-3 hover:bg-[#F5F5F5]"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <span className="text-sm text-gray-500">{availableStock} available</span>
                </div>
                <ProductUrgency stock={product.stock} />
                <Button
                  onClick={handleAddToCart}
                  className="btn-primary w-full h-14 text-lg mt-3"
                  data-testid="add-to-cart-button"
                >
                  {hasSizes && !selectedSize ? "Select a Size" : "Add to Cart"}
                </Button>
              </>
            ) : (
              <div className="bg-red-50 border-2 border-red-500 p-4 text-center">
                <p className="text-red-500 font-bold">
                  {hasSizes ? "Select a size to check availability" : "Out of Stock"}
                </p>
              </div>
            )}

            {/* Specs */}
            <WishlistButton productId={product.id} />
            {Object.keys(product.specifications || {}).length > 0 && (
              <div className="mt-8 pt-8 border-t-2 border-[#E5E5E5]">
                <h3 className="font-bold text-lg mb-4">Specifications</h3>
                <div className="space-y-2">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex justify-between py-2 border-b border-[#E5E5E5]">
                      <span className="text-gray-600">{key}</span>
                      <span className="font-bold">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Size Chart Modal */}
      <SizeChartModal
        productType={product.size_type || `${product.name} ${product.category_name}`}
        isOpen={showSizeChart}
        onClose={() => setShowSizeChart(false)}
      />
    </div>
  );
};

// Search Page
const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(query);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/products?search=${encodeURIComponent(query)}`)
      .then((res) => setProducts(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [query]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      setSearchParams({ q: searchInput.trim() });
    }
  };

  return (
    <div className="py-6 md:py-16" data-testid="search-page">
      <div className="container-main">
        {/* Search Bar - always visible */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search products..."
              className="pl-10 h-12 text-base border-2 border-[#0A0A0A]"
              data-testid="search-page-input"
            />
          </div>
          <Button type="submit" className="btn-primary h-12 px-6" data-testid="search-page-button">
            Search
          </Button>
        </form>

        <h2 className="text-2xl font-black mb-6">
          {query ? `Results for "${query}"` : "All Products"}
        </h2>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24 bg-[#F5F5F5]">
            <Search className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-bold mb-2">No products found</h3>
            <p className="text-gray-500">Try a different search term</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Checkout Page
const CheckoutPage = () => {
  const { cart, cartTotal, clearCart } = useCart();
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [shippingCost, setShippingCost] = useState(0);
  const [referralCode, setReferralCode] = useState(searchParams.get("ref") || "");
  const [referralDiscount, setReferralDiscount] = useState(0);
  const [referralApplied, setReferralApplied] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(false);
  const [activeDiscount, setActiveDiscount] = useState(null); // "referral" or "coupon"
  const [form, setForm] = useState({
    email: "",
    full_name: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: ""
  });

  const nigerianStates = [
    "Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", "Benue", "Borno",
    "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti", "Enugu", "FCT", "Gombe",
    "Imo", "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi", "Kogi", "Kwara",
    "Lagos", "Nasarawa", "Niger", "Ogun", "Ondo", "Osun", "Oyo", "Plateau",
    "Rivers", "Sokoto", "Taraba", "Yobe", "Zamfara"
  ];

  useEffect(() => {
    setShippingCost(0);
  }, [cart, form.state]);

  const applyReferralCode = async () => {
    if (!referralCode.trim() || referralApplied) return;
    if (couponApplied) { toast.error("Cannot use referral code with a coupon. Remove the coupon first."); return; }
    try {
      const res = await axios.get(`${API}/referral/check/${referralCode}`);
      if (res.data.valid) {
        setReferralDiscount(res.data.discount);
        setReferralApplied(true);
        setActiveDiscount("referral");
        toast.success(res.data.message);
      }
    } catch (err) {
      toast.error("Invalid referral code");
      setReferralDiscount(0);
    }
  };

  const applyCouponCode = async () => {
    if (!couponCode.trim() || couponApplied) return;
    if (referralApplied) { toast.error("Cannot use coupon with a referral code. Remove the referral first."); return; }
    try {
      const res = await axios.post(`${API}/coupons/validate?code=${encodeURIComponent(couponCode)}&order_total=${cartTotal}`);
      setCouponDiscount(res.data.discount_amount);
      setCouponApplied(true);
      setActiveDiscount("coupon");
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid coupon code");
      setCouponDiscount(0);
    }
  };

  const removeDiscount = () => {
    if (activeDiscount === "referral") {
      setReferralCode("");
      setReferralDiscount(0);
      setReferralApplied(false);
    } else {
      setCouponCode("");
      setCouponDiscount(0);
      setCouponApplied(false);
    }
    setActiveDiscount(null);
  };

  const totalDiscount = activeDiscount === "referral" ? referralDiscount : activeDiscount === "coupon" ? couponDiscount : 0;
  const [paymentMethod, setPaymentMethod] = useState("paystack"); // "paystack" or "bank"

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        items: cart.map((item) => ({ product_id: item.id, quantity: item.quantity, selected_size: item.selectedSize || null })),
        shipping_address: {
          full_name: form.full_name,
          phone: form.phone,
          address_line1: form.address_line1,
          address_line2: form.address_line2,
          city: form.city,
          state: form.state,
          postal_code: form.postal_code,
          country: "Nigeria"
        },
        email: form.email,
        coupon_code: activeDiscount === "coupon" ? couponCode : null
      };

      const orderRes = await axios.post(`${API}/orders`, orderData);
      const orderId = orderRes.data.id;

      // Apply referral if code was used
      if (referralApplied && referralCode) {
        try {
          await axios.post(`${API}/referral/apply?referred_email=${encodeURIComponent(form.email)}&referral_code=${encodeURIComponent(referralCode)}`);
        } catch (e) {
          console.log("Referral apply error:", e);
        }
      }

      if (paymentMethod === "paystack") {
        const paymentRes = await axios.post(`${API}/payment/initialize`, {
          order_id: orderId,
          callback_url: `${window.location.origin}/payment/callback`
        });
        clearCart();
        window.location.href = paymentRes.data.authorization_url;
      } else {
        clearCart();
        navigate(`/payment/bank-transfer?order=${orderRes.data.order_number}&total=${orderRes.data.total}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
      setLoading(false);
    }
  };

  const formatPrice = (price) => `₦${price.toLocaleString()}`;

  if (cart.length === 0) {
    return (
      <div className="container-main py-24 text-center">
        <ShoppingCart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h1 className="text-3xl font-black mb-4">Your cart is empty</h1>
        <Link to="/">
          <Button className="btn-primary">Continue Shopping</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="py-8 md:py-16" data-testid="checkout-page">
      <div className="container-main">
        <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight mb-6">Checkout</h1>
        
        {/* Task Checklist Banner - only for logged-in unverified users */}
        {customer && !customer.email_verified && customer.auth_provider !== "google" && (
          <div className="bg-[#0A0A0A] text-white p-5 mb-8 border-l-4 border-[#FF6B00]" data-testid="checkout-tasks-banner">
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-[#FF6B00]" />
              Action required to place your order:
            </h3>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-[#FF6B00] flex-shrink-0" />
              <span className="font-bold">
                <Link to="/account" className="text-[#FF6B00] underline">Verify your email address</Link>
                <span className="text-gray-400 font-normal ml-2">— check your inbox for a link from UKOK</span>
              </span>
            </div>
          </div>
        )}

        {/* Guest-friendly note */}
        {!customer && (
          <div className="bg-blue-50 border border-blue-200 p-4 mb-8 flex items-center gap-3" data-testid="guest-checkout-note">
            <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <p className="text-blue-700 text-sm">
              <strong>Guest checkout available!</strong> No account needed. Or <Link to="/login" className="text-[#FF6B00] font-bold underline">sign in</Link> to track orders and save addresses.
            </p>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* Contact */}
              <div className="bg-white border-2 border-[#0A0A0A] p-6">
                <h2 className="text-xl font-black uppercase mb-6">Contact Information</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="font-bold">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="checkout-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="font-bold">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      required
                      className="mt-1"
                      placeholder="+234..."
                      data-testid="checkout-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white border-2 border-[#0A0A0A] p-6">
                <h2 className="text-xl font-black uppercase mb-6">Shipping Address (Nigeria)</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="full_name" className="font-bold">Full Name</Label>
                    <Input
                      id="full_name"
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="checkout-fullname"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address_line1" className="font-bold">Address Line 1</Label>
                    <Input
                      id="address_line1"
                      value={form.address_line1}
                      onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="checkout-address1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address_line2" className="font-bold">Address Line 2 (Optional)</Label>
                    <Input
                      id="address_line2"
                      value={form.address_line2}
                      onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city" className="font-bold">City</Label>
                      <Input
                        id="city"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        required
                        className="mt-1"
                        data-testid="checkout-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state" className="font-bold">State</Label>
                      <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })} required>
                        <SelectTrigger className="mt-1 relative z-[100]" data-testid="checkout-state">
                          <SelectValue placeholder="Select State" />
                        </SelectTrigger>
                        <SelectContent className="z-[200]">
                          {nigerianStates.map((state) => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="postal_code" className="font-bold">Postal Code (Optional)</Label>
                    <Input
                      id="postal_code"
                      value={form.postal_code}
                      onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-[#F5F5F5] border-2 border-[#0A0A0A] p-6 sticky top-24">
                <h2 className="text-xl font-black uppercase mb-6">Order Summary</h2>
                <div className="space-y-4 mb-6">
                  {cart.map((item) => (
                    <div key={`${item.id}-${item.selectedSize || 'no-size'}`} className="flex gap-4">
                      <img src={item.images?.[0] || "/placeholder.jpg"} alt={item.name} className="w-16 h-16 object-cover" />
                      <div className="flex-1">
                        <p className="font-bold text-sm line-clamp-1">{item.name}</p>
                        {item.selectedSize && (
                          <p className="text-xs text-[#FF6B00] font-bold">Size: {item.selectedSize}</p>
                        )}
                        <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                        <p className="font-bold">{formatPrice(item.selling_price * item.quantity)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Separator className="my-4" />
                
                {/* Discount Codes */}
                <div className="mb-4">
                  {!activeDiscount && (
                    <>
                      <Label className="font-bold text-sm">Have a coupon or referral code?</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          placeholder="Coupon code"
                          className="flex-1"
                          data-testid="coupon-code-input"
                        />
                        <Button type="button" onClick={applyCouponCode} className="btn-primary" disabled={!couponCode} data-testid="apply-coupon">
                          Apply
                        </Button>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Input
                          type="text"
                          value={referralCode}
                          onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                          placeholder="Referral code (UKOK-XXXXXXXX)"
                          className="flex-1"
                          data-testid="referral-code-input"
                        />
                        <Button type="button" onClick={applyReferralCode} className="btn-outline" disabled={!referralCode} data-testid="apply-referral">
                          Apply
                        </Button>
                      </div>
                    </>
                  )}
                  {activeDiscount && (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 p-3 mt-1">
                      <div>
                        <p className="text-green-700 font-bold text-sm flex items-center gap-1">
                          <Check className="w-4 h-4" />
                          {activeDiscount === "coupon" ? `Coupon ${couponCode}` : `Referral ${referralCode}`} applied!
                        </p>
                        <p className="text-green-600 text-xs">₦{totalDiscount.toLocaleString()} off your order</p>
                      </div>
                      <button onClick={removeDiscount} className="text-red-500 text-xs font-bold hover:underline" data-testid="remove-discount">Remove</button>
                    </div>
                  )}
                </div>
                
                <Separator className="my-4" />
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span className="font-bold">{formatPrice(cartTotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Shipping</span>
                    <span className="font-bold text-green-600">FREE</span>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 p-3 text-xs text-blue-700">
                    <p className="font-bold">Shipping Note:</p>
                    <p>Delivery from GIG Logistics locations to your address may attract an additional shipping fee payable on delivery.</p>
                  </div>
                  {totalDiscount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount</span>
                      <span className="font-bold">-{formatPrice(totalDiscount)}</span>
                    </div>
                  )}
                  <Separator className="my-2" />
                  <div className="flex justify-between text-xl">
                    <span className="font-bold">Total</span>
                    <span className="font-black text-[#FF6B00]">{formatPrice(Math.max(0, cartTotal - totalDiscount))}</span>
                  </div>
                </div>
                {customer && !customer.email_verified && customer.auth_provider !== "google" ? (
                  <div className="bg-amber-50 border-2 border-amber-500 p-5 mt-6" data-testid="checkout-verify-gate">
                    <div className="flex items-start gap-3 mb-4">
                      <AlertCircle className="w-7 h-7 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-black text-lg text-amber-900 uppercase">Action Required</h3>
                        <p className="text-amber-700 text-sm">Complete the task below before you can place your order</p>
                      </div>
                    </div>
                    <div className="bg-white border-2 border-amber-300 p-4 flex items-center gap-4">
                      <div className="w-10 h-10 border-2 border-amber-400 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-amber-500" />
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-[#0A0A0A]">Verify Your Email Address</p>
                        <p className="text-sm text-gray-500">Check your inbox for a verification link from UKOK. Click it to verify.</p>
                      </div>
                      <Link to="/account" className="btn-primary px-4 py-2 text-sm font-bold flex-shrink-0 whitespace-nowrap" data-testid="go-verify-button">
                        Verify Now
                      </Link>
                    </div>
                    <p className="text-xs text-amber-600 mt-3 text-center">
                      Didn&apos;t receive the email? Go to <Link to="/account" className="font-bold underline">My Account</Link> and click &ldquo;Resend Link&rdquo;
                    </p>
                  </div>
                ) : (
                  <div className="mt-6 space-y-4">
                    {/* Payment Method Selection */}
                    <div>
                      <Label className="font-bold text-sm mb-2 block">Payment Method</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setPaymentMethod("paystack")}
                          className={`p-3 border-2 text-center transition-all ${paymentMethod === "paystack" ? "border-[#FF6B00] bg-[#FFF7ED]" : "border-[#E5E5E5] hover:border-gray-400"}`}
                          data-testid="payment-method-paystack"
                        >
                          <span className="font-bold text-sm block">Pay Online</span>
                          <span className="text-xs text-gray-500">Card, Bank, USSD</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentMethod("bank")}
                          className={`p-3 border-2 text-center transition-all ${paymentMethod === "bank" ? "border-[#FF6B00] bg-[#FFF7ED]" : "border-[#E5E5E5] hover:border-gray-400"}`}
                          data-testid="payment-method-bank"
                        >
                          <span className="font-bold text-sm block">Bank Transfer</span>
                          <span className="text-xs text-gray-500">Manual Transfer</span>
                        </button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="btn-primary w-full h-14"
                      disabled={loading}
                      data-testid="place-order-button"
                    >
                      {loading ? "Processing..." : paymentMethod === "paystack" ? "Pay with Paystack" : "Place Order & Transfer"}
                    </Button>
                    <p className="text-xs text-gray-500 text-center">
                      {paymentMethod === "paystack" ? "Secure payment powered by Paystack" : "You'll be shown bank transfer details after placing your order"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Bank Transfer Confirmation Page
const BankTransferPage = () => {
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get("order") || "";
  const total = searchParams.get("total") || "0";
  const [copied, setCopied] = useState(false);

  const bankDetails = {
    accountNumber: "1012991478",
    bankName: "Lotus Bank",
    accountName: "UKOK"
  };

  const copyAccount = () => {
    navigator.clipboard.writeText(bankDetails.accountNumber);
    setCopied(true);
    toast.success("Account number copied!");
    setTimeout(() => setCopied(false), 3000);
  };

  const whatsappMessage = encodeURIComponent(
    `Hi UKOK! I just placed order ${orderNumber} for ₦${parseFloat(total).toLocaleString()}. I've made the bank transfer and here's my payment screenshot.`
  );
  const whatsappLink = `https://wa.me/2349159596647?text=${whatsappMessage}`;

  return (
    <div className="py-8 md:py-16" data-testid="bank-transfer-page">
      <div className="container-main max-w-lg">
        <div className="border-2 border-[#0A0A0A] bg-white">
          {/* Header */}
          <div className="bg-[#0A0A0A] text-white p-6 text-center">
            <Check className="w-12 h-12 mx-auto mb-3 text-[#FF6B00]" />
            <h1 className="text-2xl font-black uppercase">Order Placed!</h1>
            <p className="text-gray-400 mt-1">Order: {orderNumber}</p>
          </div>

          {/* Bank Details */}
          <div className="p-6 space-y-6">
            <div className="bg-[#FFF7ED] border-2 border-[#FF6B00] p-5">
              <h2 className="font-black text-lg uppercase mb-4 text-center">Transfer Details</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">Amount</span>
                  <span className="font-black text-2xl text-[#FF6B00]">₦{parseFloat(total).toLocaleString()}</span>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">Bank Name</span>
                  <span className="font-bold">{bankDetails.bankName}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">Account Number</span>
                  <div className="flex items-center gap-2">
                    <span className="font-black text-lg">{bankDetails.accountNumber}</span>
                    <button onClick={copyAccount} className="text-[#FF6B00] hover:text-[#cc5500]" data-testid="copy-account-button">
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">Account Name</span>
                  <span className="font-bold">{bankDetails.accountName}</span>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="space-y-3 text-sm text-gray-600">
              <p className="font-bold text-[#0A0A0A]">How to complete your order:</p>
              <ol className="list-decimal pl-5 space-y-2">
                <li>Transfer <strong>₦{parseFloat(total).toLocaleString()}</strong> to the bank details above</li>
                <li>Take a <strong>screenshot</strong> of your payment receipt</li>
                <li>Send the screenshot to our <strong>WhatsApp support</strong> below</li>
                <li>Your order will be confirmed once payment is verified</li>
              </ol>
            </div>

            {/* WhatsApp Buttons */}
            <div className="space-y-3">
              <p className="text-xs text-center text-gray-500 font-bold">WhatsApp only — no phone calls</p>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full h-14 flex items-center justify-center gap-3 text-lg font-bold"
                data-testid="whatsapp-button"
              >
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Send to NG WhatsApp
              </a>
              <a
                href={`https://wa.me/447434779198?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline w-full h-14 flex items-center justify-center gap-3 text-lg font-bold"
                data-testid="whatsapp-uk-button"
              >
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Send to UK WhatsApp
              </a>
            </div>

            <div className="text-center">
              <Link to={`/track?order=${orderNumber}`} className="text-[#FF6B00] font-bold text-sm hover:underline">
                Track Your Order
              </Link>
              <span className="mx-2 text-gray-300">|</span>
              <Link to="/" className="text-gray-500 text-sm hover:underline">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Payment Callback
const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [status, setStatus] = useState("verifying");
  const [orderNumber, setOrderNumber] = useState("");

  useEffect(() => {
    const reference = searchParams.get("reference") || searchParams.get("trxref");
    if (reference) {
      axios.get(`${API}/payment/verify/${reference}`)
        .then((res) => {
          if (res.data.status === "success") {
            setStatus("success");
            clearCart();
            if (res.data.order_id) {
              axios.get(`${API}/orders/${res.data.order_id}`).then((orderRes) => {
                setOrderNumber(orderRes.data.order_number);
              });
            }
          } else {
            setStatus("failed");
          }
        })
        .catch(() => setStatus("failed"));
    } else {
      setStatus("failed");
    }
  }, [searchParams, clearCart]);

  return (
    <div className="container-main py-24 text-center" data-testid="payment-callback">
      {status === "verifying" && (
        <div>
          <div className="spinner mx-auto mb-4" />
          <h1 className="text-2xl font-bold">Verifying Payment...</h1>
        </div>
      )}
      {status === "success" && (
        <div>
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-black mb-4">Payment Successful!</h1>
          <p className="text-gray-600 mb-2">Thank you for your order.</p>
          {orderNumber && (
            <p className="text-lg font-bold mb-8">Order Number: <span className="text-[#FF6B00]">{orderNumber}</span></p>
          )}
          <div className="flex gap-4 justify-center">
            <Link to="/">
              <Button className="btn-outline">Continue Shopping</Button>
            </Link>
            {orderNumber && (
              <Link to={`/track?order=${orderNumber}`}>
                <Button className="btn-primary">Track Order</Button>
              </Link>
            )}
          </div>
        </div>
      )}
      {status === "failed" && (
        <div>
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-3xl font-black mb-4">Payment Failed</h1>
          <p className="text-gray-600 mb-8">Something went wrong with your payment.</p>
          <Link to="/checkout">
            <Button className="btn-primary">Try Again</Button>
          </Link>
        </div>
      )}
    </div>
  );
};

// Track Order Page
const TrackOrderPage = () => {
  const [searchParams] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(searchParams.get("order") || "");
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim()) return;
    
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/track/${orderNumber}`);
      setTracking(res.data);
    } catch (err) {
      setError("Order not found. Please check your order number.");
      setTracking(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    const orderParam = searchParams.get("order");
    if (orderParam) {
      setLoading(true);
      setError("");
      axios.get(`${API}/track/${orderParam}`)
        .then((res) => setTracking(res.data))
        .catch(() => {
          setError("Order not found. Please check your order number.");
          setTracking(null);
        })
        .finally(() => setLoading(false));
    }
  }, [searchParams]);

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-500",
      confirmed: "bg-blue-500",
      processing: "bg-purple-500",
      shipped: "bg-indigo-500",
      delivered: "bg-green-500",
      cancelled: "bg-red-500"
    };
    return colors[status] || "bg-gray-500";
  };

  return (
    <div className="py-8 md:py-16" data-testid="track-order-page">
      <div className="container-main max-w-3xl">
        <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight mb-8 text-center">Track Your Order</h1>
        
        <form onSubmit={handleTrack} className="flex gap-4 mb-12">
          <Input
            type="text"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            placeholder="Enter your order number (e.g., UKOK-20260115-ABC123)"
            className="flex-1 h-14"
            data-testid="track-order-input"
          />
          <Button type="submit" className="btn-primary h-14" disabled={loading} data-testid="track-order-button">
            {loading ? "Tracking..." : "Track"}
          </Button>
        </form>

        {error && (
          <div className="bg-red-50 border-2 border-red-500 p-4 text-center text-red-600 mb-8">
            {error}
          </div>
        )}

        {tracking && (
          <div className="border-2 border-[#0A0A0A]">
            <div className="bg-[#0A0A0A] text-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Order Number</p>
                  <p className="text-xl font-bold">{tracking.order_number}</p>
                </div>
                <div className={`px-4 py-2 text-sm font-bold uppercase ${getStatusColor(tracking.status)} text-white`}>
                  {tracking.status}
                </div>
              </div>
            </div>
            
            <div className="p-6">
              {/* Items */}
              <div className="mb-8">
                <h3 className="font-bold text-lg mb-4">Order Items</h3>
                <div className="space-y-2">
                  {tracking.items.map((item, idx) => (
                    <div key={`${item.product_id || item.name}-${idx}`} className="flex justify-between py-2 border-b border-[#E5E5E5]">
                      <span>{item.name} x {item.quantity}</span>
                      <span className="font-bold">₦{item.total.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping Address */}
              <div className="mb-8">
                <h3 className="font-bold text-lg mb-4">Shipping To</h3>
                <div className="bg-[#F5F5F5] p-4">
                  <p className="font-bold">{tracking.shipping_address.full_name}</p>
                  <p>{tracking.shipping_address.address_line1}</p>
                  {tracking.shipping_address.address_line2 && <p>{tracking.shipping_address.address_line2}</p>}
                  <p>{tracking.shipping_address.city}, {tracking.shipping_address.state}</p>
                  <p>{tracking.shipping_address.country}</p>
                </div>
              </div>

              {/* Tracking Info */}
              {tracking.tracking_info ? (
                <div>
                  <h3 className="font-bold text-lg mb-4">Tracking Updates</h3>
                  {tracking.tracking_info.carrier && (
                    <div className="mb-4 flex gap-4 text-sm">
                      <span className="text-gray-500">Carrier: <strong>{tracking.tracking_info.carrier}</strong></span>
                      {tracking.tracking_info.tracking_number && (
                        <span className="text-gray-500">Tracking #: <strong>{tracking.tracking_info.tracking_number}</strong></span>
                      )}
                    </div>
                  )}
                  <div className="space-y-4">
                    {tracking.tracking_info.history?.map((event, idx) => (
                      <div key={event.timestamp || idx} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-4 h-4 rounded-full ${idx === 0 ? "bg-[#FF6B00]" : "bg-gray-300"}`} />
                          {idx < tracking.tracking_info.history.length - 1 && (
                            <div className="w-0.5 h-full bg-gray-200 mt-1" />
                          )}
                        </div>
                        <div className="pb-6">
                          <p className="font-bold">{event.status}</p>
                          <p className="text-sm text-gray-600">{event.location}</p>
                          <p className="text-sm text-gray-500">{event.description}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(event.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 bg-[#F5F5F5]">
                  <Clock className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">Tracking information will be available once your order is shipped.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Referral Page - Share & Earn
const ReferralPage = () => {
  const [email, setEmail] = useState("");
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/referral/generate?email=${encodeURIComponent(email)}`);
      const statsRes = await axios.get(`${API}/referral/stats/${encodeURIComponent(email)}`);
      setReferralData({ ...res.data, ...statsRes.data });
    } catch (err) {
      toast.error("Something went wrong");
    }
    setLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnWhatsApp = () => {
    const text = `Shop premium UK products at UKOK! Use my referral code ${referralData.referral_code} to get ₦500 off your first order! ${referralData.share_link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareOnTwitter = () => {
    const text = `Shop premium UK products at UKOK! Use my referral code ${referralData.referral_code} to get ₦500 off your first order!`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralData.share_link)}`, '_blank');
  };

  return (
    <div className="py-8 md:py-16" data-testid="referral-page">
      <div className="container-main max-w-3xl">
        {/* Hero */}
        <div className="bg-[#0A0A0A] text-white p-8 md:p-12 mb-8 text-center">
          <Gift className="w-16 h-16 mx-auto mb-4 text-[#FF6B00]" />
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight mb-4">Share & Earn</h1>
          <p className="text-gray-400 text-lg max-w-md mx-auto">
            Refer friends to UKOK and earn <span className="text-[#FF6B00] font-bold">₦500</span> for every successful purchase they make!
          </p>
        </div>

        {/* How it works */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="text-center p-6 bg-[#F5F5F5] border-2 border-transparent hover:border-[#0A0A0A]">
            <div className="w-12 h-12 bg-[#FF6B00] text-[#0A0A0A] font-black text-xl flex items-center justify-center mx-auto mb-4">1</div>
            <h3 className="font-bold mb-2">Get Your Code</h3>
            <p className="text-sm text-gray-600">Enter your email to generate your unique referral code</p>
          </div>
          <div className="text-center p-6 bg-[#F5F5F5] border-2 border-transparent hover:border-[#0A0A0A]">
            <div className="w-12 h-12 bg-[#FF6B00] text-[#0A0A0A] font-black text-xl flex items-center justify-center mx-auto mb-4">2</div>
            <h3 className="font-bold mb-2">Share with Friends</h3>
            <p className="text-sm text-gray-600">Share your code via WhatsApp, Twitter, or direct link</p>
          </div>
          <div className="text-center p-6 bg-[#F5F5F5] border-2 border-transparent hover:border-[#0A0A0A]">
            <div className="w-12 h-12 bg-[#FF6B00] text-[#0A0A0A] font-black text-xl flex items-center justify-center mx-auto mb-4">3</div>
            <h3 className="font-bold mb-2">Earn Rewards</h3>
            <p className="text-sm text-gray-600">Get ₦500 credit when they complete their first order</p>
          </div>
        </div>

        {!referralData ? (
          <div className="bg-white border-2 border-[#0A0A0A] p-8">
            <h2 className="text-xl font-black uppercase mb-6">Get Your Referral Code</h2>
            <form onSubmit={generateCode} className="flex gap-4">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="flex-1 h-12"
                required
                data-testid="referral-email"
              />
              <Button type="submit" className="btn-primary h-12" disabled={loading} data-testid="generate-referral">
                {loading ? "Generating..." : "Get Code"}
              </Button>
            </form>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Referral Code Card */}
            <div className="bg-[#0A0A0A] text-white p-8">
              <p className="text-sm text-gray-400 uppercase tracking-wider mb-2">Your Referral Code</p>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-3xl font-black text-[#FF6B00]">{referralData.referral_code}</span>
                <button
                  onClick={() => copyToClipboard(referralData.referral_code)}
                  className="p-2 border border-white hover:bg-[#FF6B00] hover:border-[#FF6B00] transition-colors"
                >
                  <Copy className="w-5 h-5" />
                </button>
              </div>
              
              {/* Share Link */}
              <p className="text-sm text-gray-400 mb-2">Share Link</p>
              <div className="flex items-center gap-2 bg-white/10 p-3 mb-6">
                <span className="text-sm flex-1 truncate">{referralData.share_link}</span>
                <button
                  onClick={() => copyToClipboard(referralData.share_link)}
                  className="text-[#FF6B00] hover:text-white"
                >
                  <Copy className="w-5 h-5" />
                </button>
              </div>

              {/* Share Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={shareOnWhatsApp}
                  className="flex-1 bg-green-500 text-white py-3 font-bold hover:bg-green-600 transition-colors"
                >
                  Share on WhatsApp
                </button>
                <button
                  onClick={shareOnTwitter}
                  className="flex-1 bg-blue-400 text-white py-3 font-bold hover:bg-blue-500 transition-colors"
                >
                  Share on Twitter
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-[#F5F5F5] p-6 text-center border-2 border-[#0A0A0A]">
                <Users className="w-8 h-8 mx-auto mb-2 text-[#FF6B00]" />
                <p className="text-3xl font-black">{referralData.total_referrals || 0}</p>
                <p className="text-sm text-gray-600">Total Referrals</p>
              </div>
              <div className="bg-[#F5F5F5] p-6 text-center border-2 border-[#0A0A0A]">
                <Check className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p className="text-3xl font-black">{referralData.successful_referrals || 0}</p>
                <p className="text-sm text-gray-600">Successful</p>
              </div>
              <div className="bg-[#F5F5F5] p-6 text-center border-2 border-[#0A0A0A]">
                <Gift className="w-8 h-8 mx-auto mb-2 text-[#FF6B00]" />
                <p className="text-3xl font-black text-[#FF6B00]">₦{(referralData.available_rewards || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-600">Available Credit</p>
              </div>
            </div>

            {referralData.available_rewards > 0 && (
              <div className="bg-green-50 border-2 border-green-500 p-4 text-center">
                <p className="text-green-700 font-bold">
                  You have ₦{referralData.available_rewards.toLocaleString()} in rewards! Use it on your next order.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// AI Chat Widget
const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello! I'm UKOK's AI assistant. How can I help you today? I can answer questions about products, categories, shipping from UK to Nigeria, or help track your order." }
  ]);
  const [input, setInput] = useState("");
  const [orderId, setOrderId] = useState("");
  const [loading, setLoading] = useState(false);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: userMessage,
        order_id: orderId || null
      });
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (error) {
      setMessages((prev) => [...prev, { role: "assistant", content: "Sorry, I'm having trouble responding right now. Please try again." }]);
    }
    setLoading(false);
  };

  return (
    <div className="fixed bottom-20 right-6 z-[9999]" data-testid="chat-widget">
      {isOpen ? (
        <div className="bg-white border-2 border-[#0A0A0A] w-[360px] h-[500px] flex flex-col shadow-[8px_8px_0px_0px_rgba(255,107,0,1)]">
          {/* Header */}
          <div className="bg-[#0A0A0A] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-[#FF6B00] animate-pulse" />
              <span className="font-bold">UKOK Support</span>
            </div>
            <button onClick={() => setIsOpen(false)}>
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Order ID Input */}
          <div className="p-3 border-b border-[#E5E5E5]">
            <Input
              type="text"
              placeholder="Enter order number for tracking help (optional)"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              className="text-sm h-9"
            />
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div key={`${msg.role}-${idx}`} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] p-3 ${
                    msg.role === "user" 
                      ? "bg-[#0A0A0A] text-white" 
                      : "bg-[#F5F5F5] text-[#0A0A0A]"
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-[#F5F5F5] p-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 border-t-2 border-[#0A0A0A]">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Type your message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="flex-1"
                data-testid="chat-input"
              />
              <Button type="submit" className="btn-secondary" disabled={loading} data-testid="chat-send">
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-[#0A0A0A] text-white rounded-full w-14 h-14 flex items-center justify-center cursor-pointer shadow-lg transition-transform hover:scale-110"
          data-testid="chat-toggle"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-[#0A0A0A] text-white py-16" data-testid="footer">
    <div className="container-main">
      <div className="grid md:grid-cols-4 gap-8 mb-12">
        <div>
          <img
            src="https://customer-assets.emergentagent.com/job_ukok-trade-hub/artifacts/lytbo5x7_Gemini_Generated_Image_9eoaym9eoaym9eoa.jpeg"
            alt="UKOK"
            className="h-16 mb-4"
          />
          <p className="text-gray-400 mb-4">Premium UK products delivered to Nigeria with care and speed.</p>
          {/* Social Media Links */}
          <div className="flex gap-3">
            <a href="https://facebook.com/ukok.ng" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 flex items-center justify-center hover:bg-[#FF6B00] transition-colors" data-testid="social-facebook">
              <Facebook className="w-5 h-5" />
            </a>
            <a href="https://instagram.com/ukok.ng" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 flex items-center justify-center hover:bg-[#FF6B00] transition-colors" data-testid="social-instagram">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="https://twitter.com/ukok_ng" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 flex items-center justify-center hover:bg-[#FF6B00] transition-colors" data-testid="social-twitter">
              <Twitter className="w-5 h-5" />
            </a>
            <a href="https://wa.me/2349159596647" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 flex items-center justify-center hover:bg-[#25D366] transition-colors" data-testid="social-whatsapp">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </a>
            <a href="https://tiktok.com/@ukok.ng" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 flex items-center justify-center hover:bg-[#FF6B00] transition-colors" data-testid="social-tiktok">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/></svg>
            </a>
          </div>
        </div>
        <div>
          <h4 className="font-bold text-lg mb-4">Quick Links</h4>
          <ul className="space-y-2">
            <li><Link to="/" className="text-gray-400 hover:text-[#FF6B00]">Home</Link></li>
            <li><Link to="/search" className="text-gray-400 hover:text-[#FF6B00]">All Products</Link></li>
            <li><Link to="/track" className="text-gray-400 hover:text-[#FF6B00]">Track Order</Link></li>
            <li><Link to="/refer" className="text-[#FF6B00] hover:text-white font-bold">Share & Earn ₦500</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold text-lg mb-4">Support</h4>
          <ul className="space-y-2">
            <li><Link to="/faq" className="text-gray-400 hover:text-[#FF6B00]">FAQs</Link></li>
            <li><Link to="/terms" className="text-gray-400 hover:text-[#FF6B00]">Terms & Conditions</Link></li>
            <li><a href="mailto:support@ukok.com.ng" className="text-gray-400 hover:text-[#FF6B00]">support@ukok.com.ng</a></li>
            <li><a href="https://wa.me/2349159596647" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#25D366]">WhatsApp NG: +234 915 959 6647</a></li>
            <li><a href="https://wa.me/447434779198" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#25D366]">WhatsApp UK: +44 7434 779198</a></li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold text-lg mb-4">Shipping</h4>
          <p className="text-gray-400 mb-4">We ship from the UK to all 36 states in Nigeria. Delivery typically takes 7-14 business days.</p>
          <h4 className="font-bold text-lg mb-2">Contact</h4>
          <p className="text-xs text-[#FF6B00] mb-2 font-bold">WhatsApp only — no phone calls please</p>
          <ul className="space-y-1 text-gray-400 text-sm">
            <li className="flex items-center gap-2"><svg className="w-4 h-4 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> <a href="https://wa.me/2349159596647" target="_blank" rel="noopener noreferrer" className="hover:text-[#25D366]">NG: +234 915 959 6647</a></li>
            <li className="flex items-center gap-2"><svg className="w-4 h-4 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> <a href="https://wa.me/447434779198" target="_blank" rel="noopener noreferrer" className="hover:text-[#25D366]">UK: +44 7434 779198</a></li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4" /> support@ukok.com.ng</li>
          </ul>
        </div>
      </div>
      <Separator className="bg-gray-800 mb-8" />
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-gray-400 text-sm">&copy; 2026 UKOK. All rights reserved.</p>
        <div className="flex items-center gap-4 text-gray-400 text-sm">
          <Link to="/terms" className="hover:text-[#FF6B00]">Terms</Link>
          <span>&bull;</span>
          <Link to="/refund-policy" className="hover:text-[#FF6B00]">Refund Policy</Link>
        </div>
      </div>
    </div>
  </footer>
);

// FAQ Page
const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/faqs`)
      .then((res) => setFaqs(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="py-8 md:py-16" data-testid="faq-page">
      <div className="container-main max-w-3xl">
        {/* Header */}
        <div className="text-center mb-12">
          <HelpCircle className="w-16 h-16 mx-auto mb-4 text-[#FF6B00]" />
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight mb-4">Frequently Asked Questions</h1>
          <p className="text-gray-600">Find answers to common questions about shopping with UKOK</p>
        </div>

        {/* FAQ Accordion */}
        <div className="bg-white border-2 border-[#0A0A0A] mb-12">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, idx) => (
              <AccordionItem key={faq.id} value={faq.id} className="border-b border-[#E5E5E5] last:border-0">
                <AccordionTrigger className="px-6 py-4 text-left font-bold hover:text-[#FF6B00] hover:no-underline">
                  <span className="flex items-start gap-3">
                    <span className="text-[#FF6B00] font-black">{idx + 1}.</span>
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* AI Chat CTA */}
        <div className="bg-[#0A0A0A] text-white p-8 text-center">
          <MessageCircle className="w-12 h-12 mx-auto mb-4 text-[#FF6B00]" />
          <h3 className="text-2xl font-black uppercase mb-4">Still Have Questions?</h3>
          <p className="text-gray-400 mb-6">Our AI assistant is available 24/7 to help you with any questions</p>
          <Button
            onClick={() => {
              const chatWidget = document.querySelector('[data-testid="chat-toggle"]');
              if (chatWidget) chatWidget.click();
            }}
            className="btn-primary"
            data-testid="faq-chat-link"
          >
            Chat with AI Assistant
          </Button>
        </div>
      </div>
    </div>
  );
};

// Terms & Conditions Page
const TermsPage = () => {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/terms`)
      .then((res) => setContent(res.data.content || defaultTerms))
      .catch(() => setContent(defaultTerms))
      .finally(() => setLoading(false));
  }, []);

  const defaultTerms = `
# Terms and Conditions

**Last Updated: January 2026**

Welcome to UKOK. By accessing and using our website (ukok.com.ng), you agree to be bound by these Terms and Conditions.

## 1. General Terms

1.1. UKOK operates as an e-commerce platform facilitating the sale of products sourced from the United Kingdom to customers in Nigeria.

1.2. By placing an order, you confirm that you are at least 18 years old and legally capable of entering into binding contracts.

## 2. Products and Pricing

2.1. All products are sourced from authorized UK suppliers and are guaranteed authentic.

2.2. Prices are displayed in Nigerian Naira (₦) and include applicable taxes unless otherwise stated.

2.3. We reserve the right to modify prices without prior notice. The price at the time of order placement will be honored.

## 3. Orders and Payment

3.1. Orders are subject to acceptance and availability.

3.2. Payment must be completed via bank transfer using the details provided at checkout.

3.3. Orders can be cancelled within 2 hours of placement. After processing begins, cancellation may not be possible.

## 4. Shipping and Delivery

4.1. We ship to all 36 states in Nigeria plus FCT Abuja.

4.2. Standard delivery takes 7-14 business days from the UK to Nigeria.

4.3. Shipping costs are calculated based on weight and destination.

4.4. Risk of loss passes to you upon delivery.

## 5. Returns and Refunds

5.1. Unused items may be returned within 14 days of delivery in original packaging.

5.2. Refunds will be processed within 5-7 business days after receiving the returned item.

5.3. Shipping costs for returns are the responsibility of the customer unless the item was damaged or incorrect.

## 6. Intellectual Property

6.1. All content on this website, including logos, images, and text, is the property of UKOK.

6.2. You may not reproduce, distribute, or use any content without written permission.

## 7. Privacy

7.1. Your personal information is handled in accordance with our Privacy Policy.

7.2. We use cookies to enhance your browsing experience. See our Cookie Policy for details.

## 8. Limitation of Liability

8.1. UKOK is not liable for any indirect, incidental, or consequential damages arising from your use of our services.

8.2. Our total liability shall not exceed the amount paid for the specific order in question.

## 9. Governing Law

9.1. These Terms are governed by the laws of the Federal Republic of Nigeria.

9.2. Any disputes shall be resolved through arbitration in Lagos, Nigeria.

## 10. Contact Us

For questions about these Terms, please contact us at:
- Email: legal@ukok.com.ng
- Address: Lagos, Nigeria

By using UKOK, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
  `;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="py-8 md:py-16" data-testid="terms-page">
      <div className="container-main max-w-3xl">
        <div className="bg-white border-2 border-[#0A0A0A] p-8 md:p-12">
          <FileText className="w-12 h-12 mb-4 text-[#FF6B00]" />
          <div className="prose prose-lg max-w-none">
            {(content || defaultTerms).split('\n').map((line, idx) => {
              if (line.startsWith('# ')) {
                return <h1 key={`line-${idx}`} className="text-3xl font-black uppercase tracking-tight mb-6">{line.replace('# ', '')}</h1>;
              } else if (line.startsWith('## ')) {
                return <h2 key={`line-${idx}`} className="text-xl font-bold mt-8 mb-4">{line.replace('## ', '')}</h2>;
              } else if (line.startsWith('**')) {
                return <p key={`line-${idx}`} className="text-sm text-gray-500 mb-4">{line.replace(/\*\*/g, '')}</p>;
              } else if (line.match(/^\d+\.\d+\./)) {
                return <p key={`line-${idx}`} className="text-gray-600 mb-2 pl-4">{line}</p>;
              } else if (line.startsWith('- ')) {
                return <p key={`line-${idx}`} className="text-gray-600 mb-1 pl-4">{line}</p>;
              } else if (line.trim()) {
                return <p key={`line-${idx}`} className="text-gray-700 mb-4">{line}</p>;
              }
              return null;
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Refund Policy Page
const RefundPolicyPage = () => (
  <div className="py-8 md:py-16" data-testid="refund-policy-page">
    <div className="container-main max-w-3xl">
      <div className="bg-white border-2 border-[#0A0A0A] p-8 md:p-12">
        <FileText className="w-12 h-12 mb-4 text-[#FF6B00]" />
        <h1 className="text-3xl font-black uppercase tracking-tight mb-2">Refund Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last Updated: January 2026</p>

        <div className="prose max-w-none space-y-6 text-gray-700">
          <section>
            <h2 className="text-xl font-bold mt-8 mb-3 text-[#0A0A0A]">1. Eligibility for Refunds</h2>
            <p>We want you to be completely satisfied with your purchase. Refunds may be requested under the following conditions:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Item received is damaged or defective</li>
              <li>Item received does not match the product description</li>
              <li>Wrong item was delivered</li>
              <li>Item was not delivered within the estimated delivery window (plus 14 business days grace period)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-3 text-[#0A0A0A]">2. Refund Request Timeline</h2>
            <p>Refund requests must be submitted within <strong>7 days</strong> of receiving your order. Requests made after this period may not be eligible for a refund.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-3 text-[#0A0A0A]">3. How to Request a Refund</h2>
            <ol className="list-decimal pl-6 space-y-2 mt-2">
              <li>Contact our support team via WhatsApp NG: <strong>+234 915 959 6647</strong> or UK: <strong>+44 7434 779198</strong> (WhatsApp only, no calls) or email <strong>support@ukok.com.ng</strong></li>
              <li>Provide your order number and reason for the refund</li>
              <li>Include clear photos of the item (if damaged or incorrect)</li>
              <li>Our team will review your request within <strong>2-3 business days</strong></li>
            </ol>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-3 text-[#0A0A0A]">4. Refund Processing</h2>
            <p>Once your refund is approved:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Refunds will be processed to your original payment method (bank transfer)</li>
              <li>Processing time: <strong>5-10 business days</strong> after approval</li>
              <li>You will receive a confirmation email once the refund has been initiated</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-3 text-[#0A0A0A]">5. Non-Refundable Items</h2>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Items marked as &ldquo;Final Sale&rdquo; or &ldquo;Non-Returnable&rdquo;</li>
              <li>Items that have been used, washed, or altered</li>
              <li>Intimate items (underwear, swimwear) for hygiene reasons</li>
              <li>Items damaged due to customer misuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-3 text-[#0A0A0A]">6. Exchanges</h2>
            <p>We currently do not offer direct exchanges. If you need a different size or item, please request a refund and place a new order.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-3 text-[#0A0A0A]">7. Shipping Costs</h2>
            <p>Original shipping costs are non-refundable unless the return is due to our error (wrong item, defective product). Return shipping costs, if applicable, are the responsibility of the customer.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-3 text-[#0A0A0A]">8. Contact Us</h2>
            <p>For any questions about our refund policy, please contact us:</p>
            <p className="text-xs text-[#FF6B00] mt-2 font-bold">WhatsApp only — no phone calls please</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>WhatsApp NG: <a href="https://wa.me/2349159596647" className="text-[#FF6B00] font-bold hover:underline">+234 915 959 6647</a></li>
              <li>WhatsApp UK: <a href="https://wa.me/447434779198" className="text-[#FF6B00] font-bold hover:underline">+44 7434 779198</a></li>
              <li>Email: <strong>support@ukok.com.ng</strong></li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  </div>
);

// Cookie Consent Popup
const CookieConsent = () => {
  const [show, setShow] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem("ukok-cookie-consent");
    if (!consent) {
      setShow(true);
    }
  }, []);

  const acceptAll = () => {
    const consent = { necessary: true, analytics: true, marketing: true, timestamp: new Date().toISOString() };
    localStorage.setItem("ukok-cookie-consent", JSON.stringify(consent));
    setShow(false);
    toast.success("Cookie preferences saved");
  };

  const rejectOptional = () => {
    const consent = { necessary: true, analytics: false, marketing: false, timestamp: new Date().toISOString() };
    localStorage.setItem("ukok-cookie-consent", JSON.stringify(consent));
    setShow(false);
    toast.success("Only essential cookies enabled");
  };

  const savePreferences = () => {
    const consent = { ...preferences, timestamp: new Date().toISOString() };
    localStorage.setItem("ukok-cookie-consent", JSON.stringify(consent));
    setShow(false);
    setShowManage(false);
    toast.success("Cookie preferences saved");
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9998] bg-white border-t-4 border-[#FF6B00] shadow-2xl" data-testid="cookie-consent">
      {!showManage ? (
        <div className="container-main py-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <Cookie className="w-8 h-8 text-[#FF6B00] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-lg mb-1">We Value Your Privacy</h3>
                <p className="text-gray-600 text-sm">
                  We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                  By clicking &ldquo;Accept All&rdquo;, you consent to our use of cookies.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 w-full lg:w-auto">
              <Button
                onClick={() => setShowManage(true)}
                className="btn-outline text-sm flex-1 lg:flex-none"
                data-testid="manage-cookies"
              >
                Manage Cookies
              </Button>
              <Button
                onClick={rejectOptional}
                className="bg-gray-200 text-gray-700 font-bold px-4 py-2 text-sm hover:bg-gray-300 flex-1 lg:flex-none"
                data-testid="reject-cookies"
              >
                Reject Optional
              </Button>
              <Button
                onClick={acceptAll}
                className="btn-primary text-sm flex-1 lg:flex-none"
                data-testid="accept-cookies"
              >
                Accept All Cookies
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="container-main py-6">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-xl">Manage Cookie Preferences</h3>
              <button onClick={() => setShowManage(false)} className="text-gray-500 hover:text-[#0A0A0A]">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between p-4 bg-[#F5F5F5]">
                <div>
                  <h4 className="font-bold">Necessary Cookies</h4>
                  <p className="text-sm text-gray-600">Required for the website to function properly</p>
                </div>
                <span className="text-green-600 font-bold text-sm">Always Active</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#F5F5F5]">
                <div>
                  <h4 className="font-bold">Analytics Cookies</h4>
                  <p className="text-sm text-gray-600">Help us understand how visitors interact with our website</p>
                </div>
                <button
                  onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                  className={`w-12 h-6 rounded-full transition-colors ${preferences.analytics ? "bg-[#FF6B00]" : "bg-gray-300"}`}
                >
                  <span className={`block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${preferences.analytics ? "translate-x-6" : "translate-x-0.5"}`} />
                </button>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#F5F5F5]">
                <div>
                  <h4 className="font-bold">Marketing Cookies</h4>
                  <p className="text-sm text-gray-600">Used to deliver personalized advertisements</p>
                </div>
                <button
                  onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                  className={`w-12 h-6 rounded-full transition-colors ${preferences.marketing ? "bg-[#FF6B00]" : "bg-gray-300"}`}
                >
                  <span className={`block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${preferences.marketing ? "translate-x-6" : "translate-x-0.5"}`} />
                </button>
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={rejectOptional} className="btn-outline flex-1">Reject All Optional</Button>
              <Button onClick={savePreferences} className="btn-primary flex-1">Save Preferences</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


function AppRouter() {
  const location = useLocation();
  // Check for OAuth callback session_id in URL hash
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <MobileBottomNav />
      <RecentPurchasePopup />
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<><Navigation /><HomePage /><ChatWidget /><Footer /></>} />
      <Route path="/category/:slug" element={<><Navigation /><CategoryPage /><ChatWidget /><Footer /></>} />
      <Route path="/product/:slug" element={<><Navigation /><ProductDetailPage /><ChatWidget /><Footer /></>} />
      <Route path="/search" element={<><Navigation /><SearchPage /><ChatWidget /><Footer /></>} />
      <Route path="/checkout" element={<><Navigation /><CheckoutPage /><Footer /></>} />
      <Route path="/payment/callback" element={<><Navigation /><PaymentCallback /><Footer /></>} />
      <Route path="/track" element={<><Navigation /><TrackOrderPage /><ChatWidget /><Footer /></>} />
      <Route path="/refer" element={<><Navigation /><ReferralPage /><ChatWidget /><Footer /></>} />
      <Route path="/faq" element={<><Navigation /><FAQPage /><ChatWidget /><Footer /></>} />
      <Route path="/terms" element={<><Navigation /><TermsPage /><ChatWidget /><Footer /></>} />
      <Route path="/refund-policy" element={<><Navigation /><RefundPolicyPage /><ChatWidget /><Footer /></>} />
      <Route path="/payment/bank-transfer" element={<><Navigation /><BankTransferPage /><Footer /></>} />

      {/* Customer Auth Routes */}
      <Route path="/login" element={<><Navigation /><CustomerLoginPage /><Footer /></>} />
      <Route path="/account" element={<><Navigation /><CustomerAccountPage /><Footer /></>} />
      <Route path="/verify-email" element={<><Navigation /><VerifyEmailPage /><Footer /></>} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/products" element={<AdminProducts />} />
      <Route path="/admin/categories" element={<AdminCategories />} />
      <Route path="/admin/orders" element={<AdminOrders />} />
      <Route path="/admin/coupons" element={<AdminCoupons />} />
      <Route path="/admin/faqs" element={<AdminFAQs />} />
      <Route path="/admin/content" element={<AdminContent />} />
    </Routes>
    </>
  );
}

// Main App
function App() {
  return (
    <AuthProvider>
      <CustomerAuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Toaster position="top-right" />
            <CookieConsent />
            <AppRouter />
          </BrowserRouter>
        </CartProvider>
      </CustomerAuthProvider>
    </AuthProvider>
  );
}

export default App;
